import React, { useCallback, useState } from 'react'

import styled from '@emotion/styled'
import {
  AlignItems,
  AlignSelf,
  BorderStyle,
  Box,
  Button,
  ButtonColorVariant,
  Col,
  Color,
  Display,
  FlexDirection,
  Grid,
  Heading1,
  JustifyContent,
  TextAlign,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'
import Overlay from 'Overlay'
import { SocialShareSheetContainer } from 'SocialLinks/SocialShareSheet'

import { RAFBoostShare } from '../BoostedRafSharing/RAFBoostShare'
import { IRAFOffer } from '../ReferralPresentation'
import { RAFInstructions } from './RAFInstructions'
import { RafFriendGetsDiscounts } from './RafFriendGetsDiscounts'
import { RafYouGet } from './RafYouGet'

const RelativeBox = styled(Box)`
  position: relative;
  margin-bottom: -25px;
  margin-top: 40px;
`

const StickyMobileShareButton = styled(Box)`
  padding: 16px;
  background: white;
  position: sticky;
  bottom: 0;
  width: 100%;
  z-index: 2;

  @media (min-width: 768px) {
    display: none;
  }
`

const FullWidthButton = styled(Button)`
  padding: 8px 16px;
  width: 100%;
`

const PageCol = styled(Col)`
  @media (min-width: 1024px) {
    margin-left: 32px;
    margin-right: 32px;
  }
`

const RafSharingContainer = withCitrusTheme(Box, (theme) => ({
  background: theme.color(Color.White),
  width: '100%',
  padding: '32px 62px',
  borderRadius: '16px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  flexDirection: 'column',
  boxShadow: '2px 4px 8px 0px #0000000f',
  [`@media (max-width: 767px)`]: {
    display: 'none',
  },
  marginTop: '24px',
}))

const Divider = withCitrusTheme(Box, (theme) => ({
  borderColor: theme.color(Color.ColdGrey_800),
  borderLeftWidth: 0.5,
  borderTopWidth: 0.5,
  borderStyle: BorderStyle.Dashed,
}))

type RafSharingProps = {
  trackingReferFriendSocialSharing: () => void
  trackUserFreeFoodLinkShare: () => void
  trackingReferFriend: () => void
  device: string
  userRAFLink: string
  userFirstName: string
  offer: IRAFOffer
  offerTitle: string
  referralCode: string
}

export const RafSharing: React.FC<RafSharingProps> = ({
  trackingReferFriendSocialSharing,
  trackUserFreeFoodLinkShare,
  trackingReferFriend,
  device,
  userRAFLink,
  userFirstName,
  offer,
  offerTitle,
  referralCode,
}) => {
  const [isShareSheetOpen, setIsShareSheetOpen] = useState<boolean>(false)
  const firstBoxDiscount = offer.get('firstBoxDiscountFormatted') as string
  const firstMonthDiscount = offer.get('firstMonthDiscountFormatted') as string
  const secondBoxDiscount = offer.get('secondBoxDiscountFormatted') as string
  const credit = offer.get('creditFormatted') as string
  const discountDuration = offer.getIn(['discountDuration', 'formatted']) as string

  const onMobileShare = useCallback(() => {
    if (navigator.share) {
      navigator
        .share({
          title: "Here's my link to try Gousto!",
          url: userRAFLink,
          text: `Use it to get ${firstBoxDiscount} off your first box, ${secondBoxDiscount} off your second box, PLUS ${firstMonthDiscount} off all other boxes in your first ${discountDuration}.`,
        })
        .catch(() => {
          setIsShareSheetOpen(true)
        })
    } else {
      setIsShareSheetOpen(true)
    }

    trackUserFreeFoodLinkShare()
  }, [
    firstBoxDiscount,
    firstMonthDiscount,
    secondBoxDiscount,
    discountDuration,
    trackUserFreeFoodLinkShare,
    userRAFLink,
  ])

  const closeShareSheet = useCallback(() => {
    setIsShareSheetOpen(false)
    trackingReferFriendSocialSharing()
  }, [trackingReferFriendSocialSharing])

  return (
    <RelativeBox>
      <Grid
        alignItems={AlignItems.Center}
        justifyContent={JustifyContent.Center}
        flexDirection={FlexDirection.Row}
      >
        <PageCol
          size={[12, 8, 8, 8]}
          gridColumn={['1 / span 12', '1 / span 12', '3 / span 8', '1 / span 12']}
          display={Display.Flex}
          justifyContent={JustifyContent.Center}
          flexDirection={FlexDirection.Column}
          alignItems={AlignItems.Center}
        >
          <Box
            display={Display.Flex}
            alignItems={AlignItems.Center}
            flexDirection={FlexDirection.Column}
            justifyContent={JustifyContent.SpaceEvenly}
            gap={6}
            minWidth="75%"
          >
            <Heading1 textAlign={TextAlign.Center} color={Color.ColdGrey_800}>
              {offerTitle}
            </Heading1>
            <Box
              display="flex"
              flexDirection={[
                FlexDirection.Column,
                FlexDirection.Column,
                FlexDirection.Row,
                FlexDirection.Row,
              ]}
              alignItems={[
                AlignItems.Center,
                AlignItems.Center,
                AlignItems.Center,
                AlignItems.Center,
              ]}
              justifyContent={[
                JustifyContent.Center,
                JustifyContent.Center,
                JustifyContent.SpaceBetween,
                JustifyContent.SpaceBetween,
              ]}
              gap={[8, 8, 4, 4]}
              paddingBottom={4}
              style={{ width: '100%' }}
            >
              <RafYouGet credit={credit} />
              <Divider
                alignSelf={[
                  AlignSelf.Center,
                  AlignSelf.Center,
                  AlignSelf.Stretch,
                  AlignSelf.Stretch,
                ]}
                width={['280px', '280px', '1px', '1px']}
              />
              <RafFriendGetsDiscounts
                firstBoxDiscount={firstBoxDiscount}
                secondBoxDiscount={secondBoxDiscount}
                firstMonthDiscount={firstMonthDiscount}
                discountDuration={discountDuration}
              />
            </Box>
          </Box>
          <RafSharingContainer justifyContent={JustifyContent.Center}>
            <Box minWidth={['100%', '100%', '100%', '75%']}>
              <RAFBoostShare
                userRAFLink={userRAFLink}
                userFirstName={userFirstName}
                trackingReferFriendSocialSharing={trackingReferFriendSocialSharing}
                device={device}
                trackUserFreeFoodLinkShare={trackUserFreeFoodLinkShare}
                trackingReferFriend={trackingReferFriend}
                isBoost={false}
              />
            </Box>
          </RafSharingContainer>
          <RAFInstructions offer={offer} />
        </PageCol>
      </Grid>
      <StickyMobileShareButton>
        <FullWidthButton
          colorVariant={ButtonColorVariant.Primary}
          onClick={onMobileShare}
          width="100%"
        >
          Share your link
        </FullWidthButton>
      </StickyMobileShareButton>
      <Overlay open={isShareSheetOpen} from="bottom">
        <SocialShareSheetContainer onClose={closeShareSheet} referralCode={referralCode} />
      </Overlay>
    </RelativeBox>
  )
}
